import { Input } from "@fluentui/react-components";
import { AuditSession, searchResultSchema } from "../models/heretic/auditSession";
import { debounce } from "lodash";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { fetchFastApi } from "../utils/fastApi";
import { z } from "zod";
import ReactDOM from "react-dom";

interface SessionPlainTextSearchProps {
  session: AuditSession;
  onSelectedFileChange: (fileId: string) => void;
}

interface SearchResult {
  documentId: string;
  fileName: string | null;
  matchingValue: string;
  matchType: "content" | "filename";
  isAssociated: boolean;
  highlight: string;
}

export const SessionPlainTextSearch: React.FC<SessionPlainTextSearchProps> = ({ session, onSelectedFileChange }) => {
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (term: string) => {
        if (!session) {
          setSearchResults([]);
          setSelectedDocument(null);
          return;
        }
        if (term.trim() === "") {
          setSearchResults([]);
          setSelectedDocument(null);
          return;
        }

        try {
          const response = await fetchFastApi({
            method: "GET",
            path: `/matching/match-session/${session.id}/documents/search`,
            query: { query: term },
          });

          const results: Array<SearchResult> = [];

          response.documents.forEach((doc: z.infer<typeof searchResultSchema>) => {
            const isAssociated = true;
            results.push({
              documentId: doc.id,
              fileName: doc.fileName || null,
              matchingValue: doc.fileName || "",
              matchType: "filename",
              isAssociated,
              highlight: doc.highlight || "",
            });
          });
          if (results.length === 0) {
            setSelectedDocument(null);
          }

          setSearchResults(results);
          setIsPopoverOpen(results.length > 0);
        } catch (error) {
          console.error("Error searching documents:", error);
          setSearchResults([]);
          setIsPopoverOpen(false);
        }
      }, 300),
    [session]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        popoverRef.current &&
        !popoverRef.current.contains(target)
      ) {
        setIsPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate popover position when input is focused or search results change
  useEffect(() => {
    if (isPopoverOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isPopoverOpen, searchResults.length]);

  return (
    <div ref={containerRef}>
      <Input
        ref={inputRef}
        style={{ width: "300px" }}
        placeholder="Search plain text..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => searchResults.length > 0 && setIsPopoverOpen(true)}
      />
      {isPopoverOpen &&
        searchResults.length > 0 &&
        popoverPosition &&
        ReactDOM.createPortal(
          <div
            ref={popoverRef}
            className="bg-white rounded-lg shadow-lg border border-gray-200 max-h-[400px] overflow-y-auto"
            style={{
              position: "absolute",
              top: popoverPosition.top,
              left: popoverPosition.left,
              width: popoverPosition.width,
              zIndex: 99999,
            }}
          >
            {searchResults.map((result, index) => (
              <div
                key={index}
                className={`p-3 cursor-pointer transition-colors ${
                  selectedDocument === result.documentId ? "bg-gray-100" : "hover:bg-gray-50"
                } ${index !== searchResults.length - 1 ? "border-b border-gray-100" : ""}`}
                onClick={() => {
                  setSelectedDocument(result.documentId);
                  onSelectedFileChange(result.documentId);
                  setIsPopoverOpen(false);
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {result.fileName || "Unnamed Document"}
                    </div>
                  </div>
                  <div className="ml-2 flex-shrink-0">
                    {result.isAssociated ? (
                      <div className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200">
                        <span className="whitespace-nowrap">Already Added</span>
                      </div>
                    ) : (
                      <div className="inline-flex items-center px-2 py-1 rounded-full bg-white text-gray-700 text-xs border border-gray-200">
                        <span className="whitespace-nowrap">Available</span>
                      </div>
                    )}
                  </div>
                </div>
                {result.highlight && (
                  <div className="text-sm text-gray-700">
                    {result.highlight.split("...").map(
                      (fragment, i) =>
                        fragment && (
                          <div
                            key={i}
                            className="p-2 rounded bg-white border border-gray-200 mb-1 last:mb-0"
                            dangerouslySetInnerHTML={{
                              __html: fragment,
                            }}
                          />
                        )
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};
